<template>
  <div :class="[brandClasses.container, { '|hidden': upsellIdsInCart.length === upsellsWithInventory.length }]">
    <h2 v-if="title" :class="brandClasses.title">
      {{ title }}
    </h2>
    <div :class="brandClasses.upsellsWrapper">
      <product-upsell
        v-for="upsell in upsellsWithInventory"
        :key="upsell.id"
        :class="{ '|hidden': upsellIdsInCart.includes(upsell.id) }"
        data-test-id="product-upsell"
        :layout="upsellsWithInventory.length > 1 ? 'vertical' : 'horizontal'"
        :suppress-mini-cart="suppressMiniCart"
        :upsell
        @open-quickshop="$emit('openQuickshop', $event)"
        @rec-click="$emit('recClick', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Product } from '#root/api/clients/product/data-contracts'
import type { ProductUpsell } from '#types/product'

export type Props = {
  skipCheckInventory?: boolean
  suppressMiniCart?: boolean
  title?: string
  upsells: ProductUpsell[]
}

export type Emits = {
  openQuickshop: [product: Product]
  recClick: [id: string]
}

const { skipCheckInventory, upsells } = defineProps<Props>()

defineEmits<Emits>()

const { brandClasses, maxUpsells } = useAppConfig().components.product.upsells
const cart = useCartStore()
const { $gtm } = useNuxtApp()

const inventory = ref()
const isInventoryLoaded = ref(false)

const fetchInventory = async () => {
  isInventoryLoaded.value = false
  if (skipCheckInventory || !upsells?.length) {
    inventory.value = []
  }
  else {
    inventory.value = await Promise.all(
      upsells.map(({ id }) => useApi().products.$inventory(id))
    )
  }
  isInventoryLoaded.value = true
}

const upsellsWithInventory = computed(() => {
  const upsellsWithInventory = (skipCheckInventory || !isInventoryLoaded.value || !inventory.value.length)
    ? upsells
    : upsells
        .map((upsell, i) => {
          const { quantity = 0 } = inventory.value[i]?.variants[upsell.sku] || {}
          return {
            ...upsell,
            productInventoryState: quantity > 0 ? 'InStock' : 'OutOfStock'
          }
        })
        .filter(({ productInventoryState }) => productInventoryState === 'InStock')

  return upsellsWithInventory.slice(0, maxUpsells)
})

const upsellIdsInCart = computed(() => upsellsWithInventory.value
  .filter(({ id }) => cart.items.find(({ masterId }) => id === masterId))
  .map(({ id }) => id)
)

const pushCrossSellImpression = (index: number) => $gtm.push('upsell.onCrossSellImpression', upsellsWithInventory.value[index].id)

watch(() => upsells, () => fetchInventory())

onUnmounted(() => {
  upsellsWithInventory.value.forEach((_, i) => pushCrossSellImpression(i))
})
</script>
